/** Formata CPF (11 dígitos) ou CNPJ (14 dígitos) — usado na máscara do login e na exibição da área logada. */
export const formatCpfCnpj = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 11) {
        return digits
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return digits
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

/**
 * O Supabase autentica por e-mail, mas o login do consumidor é o CPF do assinante.
 * A conta é provisionada (edge function provisionar-consumidor-portal) com um e-mail
 * sintético derivado do documento — o mesmo aqui reconstrói o e-mail na hora do login.
 * O e-mail real de contato do consumidor fica em profiles.email_pessoal.
 */
export const cpfToPortalEmail = (docDigits: string): string =>
    `${docDigits}@consumidor.vigorenergy.com.br`;

/** Senha inicial provisionada: os 6 primeiros dígitos do CPF/CNPJ do assinante. */
export const senhaInicial = (docDigits: string): string => docDigits.slice(0, 6);

/** E-mail do suporte para solicitações de acesso ao portal. */
export const EMAIL_SUPORTE = 'contato@vigorenergy.com.br';

/** WhatsApp da empresa, (62) 99118-3449 — o mesmo dos CTAs do Manual do Consumidor e da Apresentação.
 *  Antes era o número de uma funcionária (#6289). */
export const WHATSAPP_SUPORTE = '5562991183449';

export const MESES_ABREV = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/**
 * Normaliza mes_referencia para o formato canônico 'JAN/2026'.
 * Cópia leve de normalizeMesReferencia de views/Financeiro/FinanceiroLayout.tsx —
 * duplicada de propósito: importar de lá arrastaria o módulo inteiro do Financeiro
 * para o bundle do portal público.
 */
export const normalizeMesReferencia = (m: unknown): string => {
    if (!m) return '';
    let s = String(m).trim().toUpperCase().replace(/\./g, '').replace(/\s/g, '');

    let mes = '';
    let ano = '';

    if (s.includes('/')) {
        const parts = s.split('/');
        if (parts.length === 2) [mes, ano] = parts;
    } else if (/^\d{5,6}$/.test(s)) {
        if (s.length === 6) {
            mes = s.substring(0, 2);
            ano = s.substring(2);
        } else {
            mes = s.substring(0, 1);
            ano = s.substring(1);
        }
    } else {
        const match = s.match(/^([A-Z]+|\d+)(\d{2,4})$/);
        if (match) [, mes, ano] = match;
        else return s;
    }

    if (!mes || !ano) return s;

    if (/^\d{1,2}$/.test(mes)) {
        const mesIndex = parseInt(mes, 10) - 1;
        if (mesIndex >= 0 && mesIndex < 12) mes = MESES_ABREV[mesIndex];
    } else {
        const foundIndex = MESES_ABREV.findIndex((nome) => mes.startsWith(nome));
        if (foundIndex !== -1) mes = MESES_ABREV[foundIndex];
    }

    if (ano.length === 2) ano = `20${ano}`;
    if (ano.length !== 4 || !MESES_ABREV.includes(mes)) return s;

    return `${mes}/${ano}`;
};

/** Ordena 'JAN/2026' cronologicamente. Retorna NaN para valores não normalizáveis. */
export const mesReferenciaParaOrdinal = (mesReferencia: string): number => {
    const normalizado = normalizeMesReferencia(mesReferencia);
    const [mes, ano] = normalizado.split('/');
    const mesIndex = MESES_ABREV.indexOf(mes);
    if (mesIndex === -1 || !/^\d{4}$/.test(ano || '')) return NaN;
    return parseInt(ano, 10) * 12 + mesIndex;
};

/** Campos de fatura usados nos cálculos de economia/custo do portal. */
export interface FaturaEconomia {
    economia?: number | null;
    custo_s_vigor?: number | null;
    total_com_vigor?: number | null;
    valor_fatura_distribuidora?: number | null;
    valor_boleto_vigor?: number | null;
}

/**
 * Economia oficial da fatura: usa faturas.economia (persistida na emissão do boleto,
 * = custo_s_vigor - total_com_vigor). Fallback legado (mesma regra de
 * gerar-pdf-boleto): quando economia está zerada mas custo_s_vigor existe,
 * economia = custo_s_vigor - (fatura distribuidora + boleto Vigor).
 */
export const economiaDaFatura = (f: FaturaEconomia): number | null => {
    if (typeof f.economia === 'number' && f.economia > 0) return f.economia;
    if (typeof f.custo_s_vigor === 'number' && f.custo_s_vigor > 0) {
        const totalPago = (f.valor_fatura_distribuidora ?? 0) + (f.valor_boleto_vigor ?? 0);
        const economia = f.custo_s_vigor - totalPago;
        return economia > 0 ? economia : null;
    }
    return null;
};

/** Custo real do mês com a Vigor (para o gráfico com/sem). */
export const custoComVigor = (f: FaturaEconomia): number | null => {
    if (typeof f.total_com_vigor === 'number' && f.total_com_vigor > 0) return f.total_com_vigor;
    const soma = (f.valor_fatura_distribuidora ?? 0) + (f.valor_boleto_vigor ?? 0);
    return soma > 0 ? soma : null;
};

/** Custo simulado do mês sem a Vigor (tarifa cheia da distribuidora). */
export const custoSemVigor = (f: FaturaEconomia): number | null => {
    if (typeof f.custo_s_vigor === 'number' && f.custo_s_vigor > 0) return f.custo_s_vigor;
    const com = custoComVigor(f);
    const economia = economiaDaFatura(f);
    if (com !== null && economia !== null) return com + economia;
    return null;
};

/** Formata WhatsApp para exibição: '5598999990000' → '(98) 99999-0000'. */
export const formatWhatsapp = (raw?: string | null): string => {
    if (!raw) return '';
    let d = raw.replace(/\D/g, '');
    if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return raw;
};

/** Formata número como moeda BRL ('—' quando ausente). */
export const brl = (v: number | null | undefined): string =>
    typeof v === 'number' ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

/** Data ISO/date → 'dd/mm/aaaa' ('—' quando ausente). */
export const dataBr = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const [ano, mes, dia] = iso.split('T')[0].split('-');
    return `${dia}/${mes}/${ano}`;
};

/** Campos de fatura que dizem o que foi cobrado no boleto. */
export interface FaturaCobranca {
    valor_boleto_vigor?: number | null;
    valor_emissao_boleto_unico?: number | null;
    valor_fatura_distribuidora?: number | null;
    equatorial_quitada_pelo_cliente?: boolean | null;
    url_boleto?: string | null;
    url_boleto_combinado?: string | null;
}

const positivo = (v: unknown): number | null => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Valor que o cliente paga no boleto (#6117). No boleto único (toda UC da Gran e as da
 * Vigor que aderiram) é `valor_emissao_boleto_unico` = energia + conta da Equatorial;
 * `valor_boleto_vigor` é só a energia e mostrava o boleto mais barato do que era (medido
 * em 27/09/2026: 126 de 142 faturas da Gran e 8 da Vigor). Fora do boleto único, segue
 * `valor_boleto_vigor`. `null` = não há cobrança para mostrar.
 */
export const valorCobradoFatura = (f: FaturaCobranca): number | null =>
    positivo(f.valor_emissao_boleto_unico) ?? positivo(f.valor_boleto_vigor);

/**
 * Link do PDF do boleto. `url_boleto_combinado` é o boleto único publicado no site
 * (layout da parceira, com a conta da Equatorial junto); `url_boleto` nas faturas da
 * parceira é a página do Asaas, que não é o documento que o cliente recebe.
 */
export const linkBoletoFatura = (f: FaturaCobranca): string | null =>
    (f.url_boleto_combinado && f.url_boleto_combinado.trim()) || (f.url_boleto && f.url_boleto.trim()) || null;

/**
 * As duas partes do boleto único, para o "Entenda seu boleto": a energia (desconto) e a
 * conta da Equatorial que vai junto. `null` quando o boleto não é único — ou quando o
 * cliente já pagou a Equatorial por fora (aí o boleto só tem a energia).
 */
export const partesBoletoUnico = (f: FaturaCobranca): { energia: number; equatorial: number } | null => {
    const total = positivo(f.valor_emissao_boleto_unico);
    const energia = positivo(f.valor_boleto_vigor) ?? 0;
    if (total === null || f.equatorial_quitada_pelo_cliente) return null;
    const equatorial = Math.round((total - energia) * 100) / 100;
    return equatorial > 0.01 ? { energia: Math.round(energia * 100) / 100, equatorial } : null;
};
